import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { ChevronDown } from 'lucide-react'
import External from '@/assets/icons/External.svg?react'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { testIdFactory } from '@/utils/test-id-factory.ts'

interface Exchange {
	name: string
	url: string
	logo?: string // Path to logo image (e.g., '/images/exchanges/fanx.webp')
}

const EXCHANGES: Exchange[] = [
	{
		name: 'Kayen',
		url: 'https://app.kayen.finance/trade/swap?in=CHZ&out=PEPPER',
		logo: '/images/exchanges/kayen.webp'
	},
	{ name: 'FANX', url: 'https://app.fanx.xyz/trade/swap', logo: '/images/exchanges/fanx.webp' },
	{ name: 'MEXC', url: 'https://www.mexc.com/exchange/PEPPER_USDT', logo: '/images/exchanges/mexc.webp' }, // Update with actual URL
	{ name: 'CoinEX', url: 'https://www.coinex.com/exchange/pepper-usdt', logo: '/images/exchanges/coinex.webp' }, // Update with actual URL
	{ name: 'Paribu', url: 'https://www.paribu.com/trade/pepper-try', logo: '/images/exchanges/paribu.webp' }, // Update with actual URL
	{ name: 'Cube', url: 'https://cube.exchange/trade/pepper', logo: '/images/exchanges/cube.webp' } // Update with actual URL
]

interface ExchangeDropdownProps {
	className?: string
}

export function ExchangeDropdown({ className }: ExchangeDropdownProps) {
	const [isOpen, setIsOpen] = useState(false)

	// Close dropdown on scroll
	useEffect(() => {
		if (!isOpen) return

		const handleScroll = () => {
			setIsOpen(false)
		}

		window.addEventListener('scroll', handleScroll, true)
		return () => window.removeEventListener('scroll', handleScroll, true)
	}, [isOpen])

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<button
					className={cn(
						'p-3 rounded-full text-[1.75rem] text-neutral relative text-center leading-none bg-danger py-5 px-10',
						className
					)}
					data-testid={testIdFactory.homeGetPepperButton()}
				>
					<div className="absolute inset-0 flex items-center justify-center pointer-events-none rounded-full" />
					<span className="flex items-center gap-2">
						Get PEPPER
						<ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
					</span>
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				sideOffset={8}
				className="w-56 bg-green-10 rounded-lg border border-neutral-50 shadow-lg p-2 z-40"
			>
				<div className="flex flex-col">
					{EXCHANGES.map(exchange => (
						<a
							key={exchange.name}
							href={exchange.url}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center justify-between px-4 py-3 rounded-md hover:bg-green-20 transition-colors text-secondary text-[1.25rem]"
							onClick={() => setIsOpen(false)}
						>
							<div className="flex items-center gap-3">
								{exchange.logo && (
									<img
										src={exchange.logo}
										alt={`${exchange.name} logo`}
										className="w-6 h-6 object-contain"
										onError={e => {
											// Hide image if it fails to load
											e.currentTarget.style.display = 'none'
										}}
									/>
								)}
								<span>{exchange.name}</span>
							</div>
							<External className="w-4 h-4" />
						</a>
					))}
				</div>
			</PopoverContent>
		</Popover>
	)
}
